import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ProductStoreProduct } from "../api/fetchProductStoreProducts/types";
import { CreateProductStoreProductRequest } from "../api/createProductStoreProduct/types";
import { ProductStoreCategory } from "../api/fetchProductStoreCategories/types";
import { fetchProductStoreCategories } from "../api/fetchProductStoreCategories/fetchProductStoreCategoriesApi";

const productSchema = z.object({
  code: z
    .string()
    .min(1, "Product code is required")
    .regex(
      /^[a-zA-Z0-9_-]+$/,
      "Code can only contain letters, numbers, hyphens, and underscores",
    ),
  name: z.string().min(1, "Product name is required"),
  description: z.string().min(1, "Description is required"),
  price: z.coerce.number().min(0, "Price must be 0 or greater"),
  type: z.enum(["physical", "digital"], {
    required_error: "Product type is required",
  }),
  categoryId: z.string().min(1, "Category is required"),
  isPublished: z.boolean(),
  thumbnailFileUuid: z.string().nullable().optional(),
});

export type ProductFormData = z.infer<typeof productSchema>;

function getDefaultValues(
  product: ProductStoreProduct | null | undefined,
): ProductFormData {
  if (!product) {
    return {
      code: "",
      name: "",
      description: "",
      price: 0,
      type: "physical",
      categoryId: "",
      isPublished: false,
      thumbnailFileUuid: null,
    };
  }

  return {
    code: product.code || "",
    name: product.name || "",
    description: product.description || "",
    price: parseFloat(product.price) || 0,
    type: product.type || "physical",
    categoryId: product.category?.id || "",
    isPublished: product.isPublished ?? false,
    thumbnailFileUuid: product.thumbnailFileUuid ?? null,
  };
}

interface UseProductFormParams {
  initialData?: ProductStoreProduct | null;
  onSubmit: (data: CreateProductStoreProductRequest) => Promise<void>;
}

export function useProductForm({
  initialData,
  onSubmit,
}: UseProductFormParams) {
  const isEditing = !!initialData;
  const [categories, setCategories] = useState<ProductStoreCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [thumbnailDisplayUrl, setThumbnailDisplayUrl] = useState<string>("");
  const [thumbnailFileName, setThumbnailFileName] = useState<string>("");
  const [isThumbnailPickerOpen, setIsThumbnailPickerOpen] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchProductStoreCategories({
          pageSize: 1000,
        });
        const activeCategories = (response.data || []).filter(
          (c) => c.isActive,
        );
        setCategories(activeCategories);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: getDefaultValues(initialData),
  });

  const { reset, handleSubmit, setValue } = form;

  useEffect(() => {
    reset(getDefaultValues(initialData));
    if (initialData?.thumbnailUrl) {
      setThumbnailDisplayUrl(initialData.thumbnailUrl);
      setThumbnailFileName("Product thumbnail");
    } else {
      setThumbnailDisplayUrl("");
      setThumbnailFileName("");
    }
  }, [initialData, reset]);

  const handleThumbnailSelected = useCallback(
    (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
      }>,
    ) => {
      if (files.length === 0) return;

      const file = files[0];

      setThumbnailDisplayUrl(file.publicUrl);
      setThumbnailFileName(file.name);

      setValue("thumbnailFileUuid", file.fileUuid, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });

      setIsThumbnailPickerOpen(false);
    },
    [setValue],
  );

  const handleRemoveThumbnail = useCallback(() => {
    setThumbnailDisplayUrl("");
    setThumbnailFileName("");

    setValue("thumbnailFileUuid", null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  }, [setValue]);

  const onFormSubmit = async (data: ProductFormData) => {
    await onSubmit({
      code: data.code,
      name: data.name,
      description: data.description,
      price: data.price,
      type: data.type,
      categoryId: data.categoryId,
      isPublished: data.isPublished,
      thumbnailFileUuid: data.thumbnailFileUuid,
    });
  };

  const openThumbnailPicker = useCallback(() => {
    setIsThumbnailPickerOpen(true);
  }, []);

  const closeThumbnailPicker = useCallback(() => {
    setIsThumbnailPickerOpen(false);
  }, []);

  return {
    form,
    isEditing,
    categories,
    loadingCategories,
    thumbnailDisplayUrl,
    thumbnailFileName,
    isThumbnailPickerOpen,
    openThumbnailPicker,
    closeThumbnailPicker,
    handleThumbnailSelected,
    handleRemoveThumbnail,
    submit: handleSubmit(onFormSubmit),
  };
}
